import { useState, Fragment } from "react";
import {
  Grid,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  formatMoney,
  formatDate,
  formatNumber,
  formatAcres,
  formatList,
  urlToDomain,
} from "./Utils";

export default function PropertyInfo(props) {
  const formatArr = (vals, fnc) =>
    vals.map((_) => ({ ..._, value: fnc(_.value) }));
  const formatRisk = (val) =>
    `${val}/10 (${val <= 3 ? "Low" : val <= 6 ? "Medium" : "High"})`;
  const t = props.estatedProperty;
  const data = {
    County: [t.property_info.county, null],
    "Unit Type": [t.property_info.unit_type, null],
    "Property Type": [
      t.property_info.property_type,
      t.property_info.property_type_meta,
    ],
    "Number Buildings": [
      t.property_info.number_buildings,
      t.property_info.number_buildings_meta,
    ],
    "Number Units": [
      t.property_info.number_units,
      t.property_info.number_units_meta,
    ],
    Beds: [
      formatNumber(t.property_info.bed),
      formatArr(t.property_info.bed_meta, formatNumber),
    ],
    Baths: [
      formatNumber(t.property_info.bath),
      formatArr(t.property_info.bath_meta, formatNumber),
    ],
    "Partial Baths": [
      formatNumber(t.property_info.partial_bath),
      formatArr(t.property_info.partial_bath_meta, formatNumber),
    ],
    "Sq Ft": [
      formatNumber(t.property_info.sqft),
      formatArr(t.property_info.sqft_meta, formatNumber),
    ],
    "Year Built": [t.property_info.year_built, t.property_info.year_built_meta],
    "Number Stories": [
      t.property_info.number_stories,
      t.property_info.number_stories_meta,
    ],
    "Pet Policy": [t.property_info.pet_policy, t.property_info.pet_policy_meta],
    "Management Company": [
      t.property_info.management_company,
      t.property_info.management_company_meta,
    ],
    "Estimated Value": [
      formatMoney(t.property_info.estimated_value),
      formatArr(t.property_info.estimated_value_meta, formatMoney),
    ],
    "Estimated Rent": [
      formatMoney(t.property_info.estimated_rent),
      formatArr(t.property_info.estimated_rent_meta, formatMoney),
    ],
    Basement: [t.property_info.basement, t.property_info.basement_meta],
    "Last Sold Price": [
      formatMoney(t.property_info.last_sold_price),
      formatArr(t.property_info.last_sold_price_meta, formatMoney),
    ],
    "Last Sold Date": [
      formatDate(t.property_info.last_sold_date),
      formatArr(t.property_info.last_sold_date_meta, formatDate),
    ],
    Pool: [t.property_info.swimming_pool ? "Yes" : "No", null],
    "Estimated Annual Tax": [
      formatMoney(t.property_info.estimated_annual_tax),
      formatArr(t.property_info.estimated_annual_tax_meta, formatMoney),
    ],
    "Lot Size": [
      formatAcres(t.property_info.lot_size),
      formatArr(t.property_info.lot_size_meta || [], formatAcres),
    ],
    "Roof Type": [
      formatList(t.property_info.roof_type),
      formatArr(t.property_info.roof_type_meta || [], formatList),
    ],
    "Exterior Construction": [
      formatList(t.property_info.exterior_construction),
      formatArr(t.property_info.exterior_construction_meta || [], formatList),
    ],
    "Foundation Type": [
      t.property_info.foundation_type,
      t.property_info.foundation_type_meta,
    ],
    "Construction Quality": [
      t.property_info.quality,
      t.property_info.quality_meta,
    ],
    Condition: [t.property_info.condition, t.property_info.condition_meta],
    "Second Mortgages": [
      t.secondMortgages.length ? "Yes" : "No",
      t.secondMortgages.map((_) => ({
        value: `${_.loan_type} (due ${_.due_date})`,
        listing_url: "https://estated.com/",
      })),
    ],
    "Walk Score": [t.property_info.walk_score, t.property_info.walk_score_meta],
    "Transit Score": [
      t.property_info.transit_score,
      t.property_info.transit_score_meta,
    ],
    "Crime Grade": [t.property_info.crime_grade, null],
    "Wild Fire Risk": [formatRisk(t.property_info.fire_risk), null],
    "Flood Risk": [formatRisk(t.property_info.flood_risk), null],
    "Heat Risk": [formatRisk(t.property_info.heat_risk), null],
    "Distance to Firehydrant": [
      props.firehydrants ? props.firehydrants : "Loading...",
      null,
    ],
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const createInfo = (header) => {
    return (
      <Fragment>
        <InfoOutlined
          id={`pi-pop-icon-${header}`}
          fontSize="inherit"
          style={{ float: "right", cursor: "pointer" }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        />
        <Popover
          open={Boolean(anchorEl && anchorEl.id === `pi-pop-icon-${header}`)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" color="primary" gutterBottom>
              {header}
            </Typography>
            <Table size="small">
              <TableBody>
                {data[header][1].map((a, index) => {
                  const addr = [
                    ...(t.scraper.address || []),
                    { listing_url: "https://estated.com/", value: "Estated" },
                  ].find((_) => _.listing_url === a.listing_url).value;
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        {a.listing_url
                          ? urlToDomain(a.listing_url)
                          : a.updated_at.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <a
                          href={a.listing_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {typeof addr === "string"
                            ? addr
                            : `${addr.street_address}, ${addr.city}, ${addr.state} ${addr.zip_code}`}
                        </a>
                      </TableCell>
                      <TableCell>{a.value}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </Popover>
      </Fragment>
    );
  };

  const createTable = (keys) => {
    return (
      <Table size="small">
        <TableBody>
          {keys.map((header, index) => (
            <TableRow key={index}>
              <TableCell>{header}</TableCell>
              <TableCell>
                {data[header][0]}
                {data[header][1]?.length ? createInfo(header) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const half = Math.ceil(Object.keys(data).length / 2);
  return (
    <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
      <Typography variant="h6" color="primary" gutterBottom>
        Property Info
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          {createTable(Object.keys(data).slice(0, half))}
        </Grid>
        <Grid item xs={6}>
          {createTable(Object.keys(data).slice(half))}
        </Grid>
      </Grid>
    </Paper>
  );
}
